import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";

import ActivationSummary from "./ActivationSummary";
import ActivationTeamsTable from "./ActivationTeamsTable";
import { getSchoolActivationStats } from "../../crud/api/activation.crud";

const useStyles = makeStyles(() => ({
  schoolMissingPaper: { padding: 16, marginBottom: 16 },
  loadingText:        { marginLeft: 12 },
}));

// TZ-786 Step 5: orchestrator page. Shows the school-wide rollup plus
// a per-team breakdown for whichever school the admin has selected in
// the SchoolSelector. Backend decides visibility per role — Zone Super
// Admin and Org Admin see every team; a coach only sees their own
// teams via TeamStaffMember (see Api::V3::ActivationController
// visible_teams_for). Reset on school switch so the previous school's
// data doesn't linger.

const Activation = ({ school }) => {
  const classes = useStyles();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const schoolId = school && school.id;
  const schoolMissing = !schoolId;

  useEffect(() => {
    setStats(null);
    if (schoolMissing) return;
    setLoading(true);
    let cancelled = false;
    getSchoolActivationStats((json) => {
      if (!cancelled) setStats(json);
    }, schoolId)
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [schoolId, schoolMissing]);

  return (
    <div>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Activation Tracking
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Who's signed into the app vs. who's still on the roster but
          hasn't activated yet. Activation = first successful login;
          new rows from a roster upload start as Unactivated and flip
          to Activated the first time the student opens the app.
        </Typography>
      </Box>

      {schoolMissing && (
        <Paper className={classes.schoolMissingPaper}>
          <Typography color="secondary">
            Select a school first to load activation stats.
          </Typography>
        </Paper>
      )}

      {!schoolMissing && loading && (
        <Box display="flex" alignItems="center" py={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" className={classes.loadingText}>
            Loading activation stats…
          </Typography>
        </Box>
      )}

      {!schoolMissing && !loading && stats && (
        <>
          <ActivationSummary summary={stats.summary} />
          <ActivationTeamsTable perTeam={stats.per_team} />
        </>
      )}
    </div>
  );
};

Activation.propTypes = {
  school: PropTypes.shape({
    id:   PropTypes.number,
    name: PropTypes.string,
  }),
};

Activation.defaultProps = {
  school: null,
};

const mapStateToProps = (store) => ({
  school: store.school.selected_school,
});

export default connect(mapStateToProps)(Activation);
